import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getMyListings, getShowings, getLeases, deleteListing, updateShowing, stripeConnectOnboard, getStripeConnectStatus, stripeConnectDisconnect, photoUrl } from "../lib/api";
import Navbar from "../components/Navbar";
import LeaseCard from "../components/LeaseCard";
import OfferLeaseDialog from "../components/OfferLeaseDialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Plus, Home, Calendar, FileText, Eye, Trash2, CheckCircle, X, Clock, MapPin, CreditCard, ExternalLink, Loader2, Unlink, FileSignature
} from "lucide-react";

export default function LandlordDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [showings, setShowings] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [offerLeaseShowing, setOfferLeaseShowing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [listRes, showRes, leaseRes, stripeRes] = await Promise.all([
          getMyListings(), getShowings(), getLeases(), getStripeConnectStatus()
        ]);
        setListings(listRes.data);
        setShowings(showRes.data);
        setLeases(leaseRes.data);
        setStripeStatus(stripeRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, []);

  // Check if returning from Stripe onboarding
  useEffect(() => {
    if (searchParams.get("stripe") === "complete") {
      getStripeConnectStatus().then(res => setStripeStatus(res.data)).catch(() => {});
    }
  }, [searchParams]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await deleteListing(id);
      setListings(prev => prev.filter(l => l.listing_id !== id));
    } catch (err) { console.error(err); }
  };

  const handleShowingAction = async (id, status) => {
    try {
      await updateShowing(id, status);
      setShowings(prev => prev.map(s => s.showing_id === id ? { ...s, status } : s));
    } catch (err) { console.error(err); }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0d0d0d] font-['Fraunces']" data-testid="landlord-dashboard-heading">
              Property Dashboard
            </h1>
            <p className="text-[#5a5a5a] mt-1">Manage your listings and tenants</p>
          </div>
          <Button data-testid="create-listing-btn" onClick={() => navigate("/create-listing")} className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold px-6">
            <Plus className="w-4 h-4 mr-2" /> New Listing
          </Button>
        </div>

        {/* Stripe Connect Card */}
        <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm mb-6" data-testid="stripe-connect-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stripeStatus?.onboarding_complete ? "bg-[#1a6b3c]/10" : "bg-[#e8633a]/10"}`}>
                <CreditCard className={`w-5 h-5 ${stripeStatus?.onboarding_complete ? "text-[#1a6b3c]" : "text-[#e8633a]"}`} />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Stripe Payouts</p>
                <p className="text-xs text-[#888]">
                  {stripeStatus?.onboarding_complete
                    ? "Connected — you can receive payments"
                    : stripeStatus?.connected
                      ? "Setup incomplete — finish Stripe onboarding"
                      : "Connect Stripe to receive rent payments"}
                </p>
              </div>
            </div>
            {stripeStatus?.onboarding_complete ? (
              <div className="flex items-center gap-2">
                <Badge className="bg-[#1a6b3c]/10 text-[#1a6b3c] border-0 rounded-full text-xs flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Active
                </Badge>
                <Button
                  data-testid="stripe-disconnect-btn"
                  onClick={async () => {
                    if (!window.confirm("Disconnect your Stripe account? You won't receive payouts until you reconnect.")) return;
                    try {
                      await stripeConnectDisconnect();
                      setStripeStatus({ connected: false, onboarding_complete: false });
                    } catch (err) { console.error(err); }
                  }}
                  size="sm" variant="ghost"
                  className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full px-3"
                >
                  <Unlink className="w-3 h-3 mr-1" /> Disconnect
                </Button>
              </div>
            ) : (
              <Button
                data-testid="stripe-connect-btn"
                onClick={async () => {
                  setStripeLoading(true);
                  try {
                    const res = await stripeConnectOnboard(window.location.origin);
                    window.location.href = res.data.url;
                  } catch (err) { console.error(err); }
                  setStripeLoading(false);
                }}
                disabled={stripeLoading}
                size="sm"
                className="rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white text-xs px-5"
              >
                {stripeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Connect Stripe <ExternalLink className="w-3 h-3 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1a6b3c]/10 flex items-center justify-center">
                <Home className="w-5 h-5 text-[#1a6b3c]" />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Active Listings</p>
                <p className="text-2xl font-bold text-[#0d0d0d]">{listings.filter(l => l.active).length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1a6b3c]/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#1a6b3c]" />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Pending Showings</p>
                <p className="text-2xl font-bold text-[#0d0d0d]">{showings.filter(s => s.status === "pending").length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#1a6b3c]/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#1a6b3c]" />
              </div>
              <div>
                <p className="font-semibold text-[#0d0d0d] text-sm">Active Leases</p>
                <p className="text-2xl font-bold text-[#0d0d0d]">{leases.filter(l => l.status === "active").length}</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList className="bg-white border border-[#d5d3ce]/60 rounded-xl p-1">
            <TabsTrigger value="listings" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="tab-my-listings">
              <Home className="w-4 h-4 mr-1" /> Listings
            </TabsTrigger>
            <TabsTrigger value="showings" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="tab-landlord-showings">
              <Calendar className="w-4 h-4 mr-1" /> Showings
            </TabsTrigger>
            <TabsTrigger value="leases" className="rounded-lg data-[state=active]:bg-[#1a6b3c] data-[state=active]:text-white" data-testid="tab-landlord-leases">
              <FileText className="w-4 h-4 mr-1" /> Leases
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            {listings.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-[#d5d3ce]/60 text-center">
                <Home className="w-10 h-10 text-[#d5d3ce] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0d0d0d] mb-2">No listings yet</h3>
                <p className="text-[#5a5a5a] text-sm mb-4">Create your first listing to start receiving applications.</p>
                <Button onClick={() => navigate("/create-listing")} className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold">
                  <Plus className="w-4 h-4 mr-2" /> Create Listing
                </Button>
              </div>
            ) : (
              <div className="space-y-3" data-testid="my-listings-list">
                {listings.map(l => (
                  <div key={l.listing_id} className="bg-white rounded-xl p-4 border border-[#d5d3ce]/60 flex items-center gap-4">
                    <img
                      src={photoUrl(l.photos?.[0]) || "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?w=200"}
                      alt=""
                      className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-[#0d0d0d] truncate">{l.title}</p>
                        {l.verified && l.active ? (
                          <Badge className="bg-[#1a6b3c]/10 text-[#1a6b3c] border-0 rounded-full text-[10px] font-semibold">
                            <CheckCircle className="w-3 h-3 mr-1" /> Live
                          </Badge>
                        ) : l.active ? (
                          <Badge className="bg-amber-100 text-amber-900 border-0 rounded-full text-[10px] font-semibold">
                            <Clock className="w-3 h-3 mr-1" /> Pending review
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 border-0 rounded-full text-[10px] font-semibold">
                            Needs changes
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#5a5a5a] mt-1">
                        <MapPin className="w-3.5 h-3.5" /> {l.city}
                        <span className="font-medium text-[#1a6b3c]">${l.price}/mo</span>
                      </div>
                      {!l.verified && l.active && (
                        <p className="text-xs text-amber-700 mt-1.5">
                          We're reviewing your listing. You'll get an email once it's live (usually within 24h).
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/listing/${l.listing_id}`)} className="text-[#5a5a5a]">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(l.listing_id)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="showings">
            {showings.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-[#d5d3ce]/60 text-center">
                <Calendar className="w-10 h-10 text-[#d5d3ce] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0d0d0d] mb-2">No showing requests</h3>
                <p className="text-[#5a5a5a] text-sm">Showing requests from renters will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {showings.map(s => (
                  <div key={s.showing_id} className="bg-white rounded-xl p-4 border border-[#d5d3ce]/60">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#0d0d0d]">{s.listing_title}</p>
                        <p className="text-sm text-[#5a5a5a]">Renter: {s.renter_name}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-[#5a5a5a]">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {s.date}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.time}</span>
                        </div>
                      </div>
                      {s.status === "pending" ? (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleShowingAction(s.showing_id, "confirmed")} className="rounded-full bg-[#1a6b3c] text-white text-xs">
                            <CheckCircle className="w-3.5 h-3.5 mr-1" /> Confirm
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleShowingAction(s.showing_id, "cancelled")} className="rounded-full text-xs border-red-300 text-red-600">
                            <X className="w-3.5 h-3.5 mr-1" /> Decline
                          </Button>
                        </div>
                      ) : s.status === "confirmed" ? (
                        <div className="flex items-center gap-2">
                          <Badge className="rounded-full text-xs bg-[#1a6b3c]/10 text-[#1a6b3c] border-0">confirmed</Badge>
                          <Button
                            data-testid={`offer-lease-btn-${s.showing_id}`}
                            size="sm"
                            onClick={() => setOfferLeaseShowing(s)}
                            className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] text-xs font-semibold"
                          >
                            <FileSignature className="w-3.5 h-3.5 mr-1" /> Offer Lease
                          </Button>
                        </div>
                      ) : (
                        <Badge className={`rounded-full text-xs ${s.status === "completed" ? "bg-[#1a6b3c]/10 text-[#1a6b3c]" : "bg-red-100 text-red-600"} border-0`}>
                          {s.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="leases">
            {leases.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-[#d5d3ce]/60 text-center">
                <FileText className="w-10 h-10 text-[#d5d3ce] mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-[#0d0d0d] mb-2">No leases yet</h3>
                <p className="text-[#5a5a5a] text-sm">Confirm a showing and click <strong>Offer Lease</strong> to send a digital lease. It will appear here with signing status.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="landlord-leases-list">
                {leases.map((l) => (
                  <LeaseCard
                    key={l.lease_id}
                    lease={l}
                    currentUserId={user?.user_id}
                    onUpdate={(updated) =>
                      setLeases((prev) => prev.map((x) => (x.lease_id === updated.lease_id ? updated : x)))
                    }
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {offerLeaseShowing && (
        <OfferLeaseDialog
          open={!!offerLeaseShowing}
          onOpenChange={(open) => !open && setOfferLeaseShowing(null)}
          showing={offerLeaseShowing}
          listing={listings.find((l) => l.listing_id === offerLeaseShowing.listing_id)}
          onLeaseCreated={(lease) => {
            setLeases((prev) => [lease, ...prev]);
            setOfferLeaseShowing(null);
          }}
        />
      )}
    </div>
  );
}
